import { z } from 'zod';
import { businessDaysBetween, todayIn } from '@xiltrion/calendario';
import { codeSchema } from '../code.js';
/** SLA policy. Values are configuration, never code. */
export const escalationRuleSchema = z.object({
    /** Negative = before due; positive = after due. Minutes. */
    offsetMinutes: z.number().int(),
    /**
     * WARNING: aviso al responsable. NOTIFY_SUPERVISOR: aviso a su superior en la cadena de mando.
     * ESCALATE: marca el caso como escalado y avisa al superior. REASSIGN: reasigna. Sin superior, avisa a los administradores.
     */
    action: z.enum(['WARNING', 'NOTIFY_SUPERVISOR', 'ESCALATE', 'REASSIGN']),
    notifyAssignee: z.boolean().default(true),
});
export const slaPolicySchema = z.object({
    code: codeSchema(60),
    name: z.string().min(1).max(120),
    amount: z.number().int().min(1),
    unit: z.enum(['MINUTES', 'HOURS', 'DAYS']),
    timeMode: z.enum(['CALENDAR', 'BUSINESS']).default('BUSINESS'),
    calendarCode: z.string().optional(),
    escalations: z.array(escalationRuleSchema).default([]),
    /** Optional selectors so a policy can apply by case type/channel/priority when the node has none. */
    appliesTo: z
        .object({
        caseTypeCodes: z.array(z.string()).optional(),
        channels: z.array(z.string()).optional(),
        priorities: z.array(z.string()).optional(),
    })
        .optional(),
});
export const businessCalendarSchema = z.object({
    code: codeSchema(60),
    name: z.string().min(1),
    timezone: z.string().default('America/Bogota'),
    /** 0=Sunday … 6=Saturday, each with optional working window. */
    workingDays: z
        .array(z.object({ weekday: z.number().int().min(0).max(6), start: z.string().regex(/^\d{2}:\d{2}$/), end: z.string().regex(/^\d{2}:\d{2}$/) }))
        .min(1),
    holidays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).default([]),
});
/** Festivos de Colombia y años que se cargan de una vez: del paquete de la suite (`@xiltrion/calendario`). */
export { COLOMBIAN_HOLIDAY_YEARS_AHEAD, colombianHolidays, colombianHolidaysBetween } from '@xiltrion/calendario';
export const SlaInstanceStatus = {
    RUNNING: 'RUNNING',
    WARNING: 'WARNING',
    BREACHED: 'BREACHED',
    MET: 'MET',
    CANCELLED: 'CANCELLED',
    PAUSED: 'PAUSED',
};
export const CASE_TERM_WARN_DAYS = 3;
export const TASK_DUE_WARN_DAYS = 1;
/** Hora en 12 horas como la lee la gente: «5:00 P.M.», «8:00 A.M.» (nunca «17:00»). */
const timeIn = (d, tz) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase().startsWith('P') ? 'P.M.' : 'A.M.'}`;
};
const dayIn = (d, tz) => new Intl.DateTimeFormat('es-CO', { timeZone: tz, weekday: 'long' }).format(d);
const shortDateIn = (d, tz) => new Intl.DateTimeFormat('es-CO', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short' }).format(d);
const businessDaysText = (n) => `${n} ${n === 1 ? 'día hábil' : 'días hábiles'}`;
const nextDay = (ymd) => { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); };
/**
 * Formats remaining time the way the UI must show it. Con calendario cuenta días hábiles entre hoy y el día del
 * vencimiento (hoy, mañana, el lunes, en N días hábiles; vencida hace N días hábiles); sin él, horas corridas.
 */
export function describeDue(dueAt, now = new Date(), opts = {}) {
    const diff = dueAt.getTime() - now.getTime();
    const cal = opts.calendar;
    if (cal) {
        const tz = cal.timezone;
        const today = todayIn(tz, now);
        const dueDay = todayIn(tz, dueAt);
        const days = businessDaysBetween(today, dueDay, cal);
        const warn = opts.warnBusinessDays ?? CASE_TERM_WARN_DAYS;
        if (diff < 0) {
            const text = dueDay === today ? `Venció hoy a las ${timeIn(dueAt, tz)}` : days === 0 ? `Vencida el ${shortDateIn(dueAt, tz)}` : `Vencida hace ${businessDaysText(-days)}`;
            return { text, tone: 'overdue', businessDays: days };
        }
        const text = dueDay === today ? `Vence hoy a las ${timeIn(dueAt, tz)}`
            : days === 0 ? `Vence el ${shortDateIn(dueAt, tz)}`
                : days === 1 ? (dueDay === nextDay(today) ? 'Vence mañana' : `Vence el ${dayIn(dueAt, tz)}`)
                    : `Vence en ${businessDaysText(days)}`;
        return { text, tone: days <= warn ? 'warning' : 'ok', businessDays: days };
    }
    const abs = Math.abs(diff);
    const d = Math.floor(abs / 86_400_000);
    const h = Math.floor((abs % 86_400_000) / 3_600_000);
    const m = Math.floor((abs % 3_600_000) / 60_000);
    const parts = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
    if (diff < 0)
        return { text: `Vencida hace ${parts}`, tone: 'overdue' };
    if (diff < 24 * 3_600_000)
        return { text: `Vence en ${parts}`, tone: 'warning' };
    return { text: `Vence en ${parts}`, tone: 'ok' };
}
//# sourceMappingURL=sla.js.map